import React, { createContext, useContext, ReactNode } from 'react';
import { useNotificationPreferences, UseNotificationPreferencesReturn } from '@/hooks/useNotificationPreferences';

// Create the context
const NotificationPreferencesContext = createContext<UseNotificationPreferencesReturn | undefined>(undefined);

// Provider component props
interface NotificationPreferencesProviderProps {
  children: ReactNode;
}

/**
 * Provider component that wraps the application and provides notification preferences
 * state and actions to all child components through React Context.
 */
export function NotificationPreferencesProvider({ children }: NotificationPreferencesProviderProps) {
  const notificationPreferences = useNotificationPreferences();

  return (
    <NotificationPreferencesContext.Provider value={notificationPreferences}>
      {children}
    </NotificationPreferencesContext.Provider>
  );
}

/**
 * Custom hook to consume the NotificationPreferencesContext.
 * This hook provides access to notification preferences state and actions.
 * 
 * @throws {Error} If used outside of NotificationPreferencesProvider
 * @returns {UseNotificationPreferencesReturn} The notification preferences state and actions
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { preferences, updateWhatsAppPreferences, isLoading } = useNotificationPreferencesContext();
 *   
 *   const handleToggleWhatsApp = async () => {
 *     await updateWhatsAppPreferences({ enabled: !preferences.whatsapp.enabled });
 *   };
 *   
 *   if (isLoading) return <div>Loading...</div>;
 *   
 *   return (
 *     <button onClick={handleToggleWhatsApp}>
 *       WhatsApp: {preferences.whatsapp.enabled ? 'On' : 'Off'}
 *     </button>
 *   );
 * }
 * ```
 */
export function useNotificationPreferencesContext(): UseNotificationPreferencesReturn {
  const context = useContext(NotificationPreferencesContext);
  
  if (context === undefined) {
    throw new Error(
      'useNotificationPreferencesContext must be used within a NotificationPreferencesProvider. ' +
      'Make sure to wrap your component tree with <NotificationPreferencesProvider>.'
    );
  }
  
  return context;
}

// Export the context for advanced use cases (testing, etc.)
export { NotificationPreferencesContext };

// Type exports for convenience
export type { UseNotificationPreferencesReturn, NotificationPreferencesProviderProps };